"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { Search as SearchIcon, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { createClient } from "@/lib/supabase/client";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

interface Product {
  id: number;
  name: string;
  slug: string;
  price: number;
  category_id: number;
  categories: {
    name: string;
  };
}

export default function MobileSearch() {
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();

  useEffect(() => {
    const searchProducts = async () => {
      if (!query) {
        setProducts([]);
        return;
      }
      
      setLoading(true);
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('products')
          .select(`
            id,
            name,
            slug,
            price,
            category_id,
            categories (
              name
            )
          `)
          .ilike('name', `%${query}%`)
          .limit(5);

        if (error) throw error;
        setProducts(data || []);
      } catch (error) {
        console.error('Error searching products:', error);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(searchProducts, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleSelect = (product: Product) => {
    router.push(`/product/${product.slug}`);
    setQuery("");
    setProducts([]);
  };

  const clearSearch = () => {
    setQuery("");
    setProducts([]);
    inputRef.current?.focus();
  };

  return (
    <div className="relative w-full px-4">
      <div className="relative max-w-3xl mx-auto">
        <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search products..."
          className="w-full pl-10 pr-4 py-2"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 200)}
        />
        {query && (
          <button
            onClick={clearSearch}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Search results dropdown */}
      {(products.length > 0 || loading) && isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-md">
          <div className="p-2">
            {loading ? (
              <div className="flex items-center justify-center p-4">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : products.length > 0 ? (
              <div className="space-y-1">
                {products.map((product) => (
                  <button
                    key={product.id}
                    className="flex w-full items-center justify-between rounded-sm px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
                    onClick={() => handleSelect(product)}
                  >
                    <span className="truncate">{product.name}</span>
                    <span className="ml-2 whitespace-nowrap text-sm text-muted-foreground">
                      {formatPrice(product.price)}
                    </span>
                  </button>
                ))}
              </div>
            ) : query ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                No products found
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
